import * as React from 'react';
import { SimpleTooltip } from '../tooltip/prebuilt/simple-tooltip/simple-tooltip';
import type { TagProps, TAllowedColors } from './types';
import { cn } from '@utils';
import { isTextTruncated } from 'src/utils/styles/styles.util';

const TagColorsMapper = {
	yellow: {
		primary: 'text-[#7C5B00]',
		background: 'bg-[#FEF4D9]',
		border: 'border-[#F7B50020]',
	},
	red: {
		primary: 'text-[#740D0D]',
		background: 'bg-[#FBE4E4]',
		border: 'border-[#E0202020]',
	},
	green: {
		primary: 'text-[#126438]',
		background: 'bg-[#E5F8EE]',
		border: 'border-[#24C87520]',
	},
	purple: {
		primary: 'text-purple-600',
		background: 'bg-purple-50',
		border: 'border-purple-600',
	},
	blue: {
		primary: 'text-blue-500',
		background: 'bg-blue-50',
		border: 'border-blue-500',
	},
	gray: {
		primary: 'text-gray-500',
		background: 'bg-gray-100',
		border: 'border-gray-500',
	},
	darkGray: {
		primary: 'text-white',
		background: 'bg-gray-500/50',
		border: 'border-gray-900',
	},
	liteGray: {
		primary: 'text-gray-400',
		background: 'bg-gray-100',
		border: 'border-gray-400',
	},
	disabledGray: {
		primary: 'text-gray-300',
		background: 'bg-gray-100',
		border: 'border-gray-300',
	},
	tertiary: {
		primary: 'text-white',
		background: 'bg-gray-400',
		border: 'border-gray-800',
	},
	pink: {
		primary: 'text-pink-500',
		background: 'bg-pink-50',
		border: 'border-pink-500',
	},
	turquoise: {
		primary: 'text-cyan-500',
		background: 'bg-cyan-50',
		border: 'border-cyan-500',
	},
	orange: {
		primary: 'text-[#7D3C0F]',
		background: 'bg-[#FEEDE2]',
		border: 'border-[#FA771E20]',
	},
	teal: {
		primary: 'text-[#004040]',
		background: 'bg-[#D9ECEC]',
		border: 'border-[#AED6D6]',
	},
	navy: {
		primary: 'text-[#1A1D6D]',
		background: 'bg-[#E9E9FB]',
		border: 'border-[#C5C6F4]',
	},
	'golden-brown': {
		primary: 'text-[#5C4305]',
		background: 'bg-[#F5EDDB]',
		border: 'border-[#B8860B33]',
	},
	indigo: {
		primary: 'text-[#4B0082]',
		background: 'bg-[#8A2BE233]',
		border: 'border-[#4B008220]',
	},
	steelBlue: {
		primary: 'text-[#254871]',
		background: 'bg-[#EDF4FC]',
		border: 'border-[#25487120]',
	},
	aqua: {
		primary: 'text-[#1A6363]',
		background: 'bg-[#E5F8F8]',
		border: 'border-[#1A636320]',
	},
};

export const DEFAULT_TAG_FONT_WEIGHT = 'font-semibold';
export const DEFAULT_TAG_PADDING = 'px-1.5';
export const DEFAULT_TAG_TYPE = 'basic';

export const Tag = React.forwardRef<HTMLDivElement, TagProps>(
	(
		{
			children,
			color,
			fontWeight = DEFAULT_TAG_FONT_WEIGHT,
			padding = DEFAULT_TAG_PADDING,
			truncate = false,
			type = DEFAULT_TAG_TYPE,
			leftIcon,
			rightIcon,
			className,
			...props
		},
		ref
	) => {
		const textRef = React.useRef<HTMLSpanElement | null>(null);
		const [isTruncated, setIsTruncated] = React.useState(false);

		React.useEffect(() => {
			if (textRef.current && truncate) {
				setIsTruncated(isTextTruncated(textRef.current));
			}
		}, [children, truncate]);

		if (!children) return null;

		const baseClasses = [
			'rounded-full',
			'font-sans',
			'text-xs',
			'leading-3',
			'capitalize',
			'w-fit',
			'h-fit',
			'whitespace-nowrap',
			'flex',
			'items-center',
			'flex-row',
			'gap-1',
			padding,
			fontWeight,
			className,
		];

		const colorClasses = [
			TagColorsMapper[color].background,
			TagColorsMapper[color].primary,
			TagColorsMapper[color].border,
			'border',
		];

		return (
			<div {...props} className={cn([...baseClasses, ...colorClasses].join(' '), className)} ref={ref}>
				{leftIcon}
				{isTruncated ? (
					<SimpleTooltip content={children}>
						<TagContent ref={textRef} truncate={truncate}>
							{children}
						</TagContent>
					</SimpleTooltip>
				) : (
					<TagContent ref={textRef} truncate={truncate}>
						{children}
					</TagContent>
				)}
				{rightIcon && rightIcon}
			</div>
		);
	}
);

const TagContent = React.forwardRef<HTMLSpanElement, { children: React.ReactNode; truncate: boolean }>(
	({ children, truncate }, ref) => (
		<span className={cn('block py-[2px]', truncate && 'max-w-[150px] truncate')} ref={ref}>
			{children}
		</span>
	)
);

TagContent.displayName = 'TagContent';
Tag.displayName = 'Tag';

export { TAllowedColors };
